import { validate as isUUID } from 'uuid';

import LocalStorageService from '~/services/localStorage.service';

import { log, LOG } from '~/utils/logging';

/**
 * Reads a persisted id list from localStorage and drops every entry that is
 * not a UUID, writing the cleaned list back so the stored copy converges to
 * valid data. The server-side localStorage backup snapshots localStorage
 * periodically, so it converges automatically as well.
 *
 * Older app versions persisted site/cost center names instead of ids under
 * these keys; passing a name to a UUID-validated endpoint throws a request
 * validation error on every page load (DNI-848).
 *
 * @param localStorageKey - The LocalStorageService key holding the id list.
 * @returns The stored ids with all non-UUID entries removed.
 */
export function getStoredUuidList(localStorageKey: string): string[] {
  const storedValues =
    LocalStorageService.getArrayFromLocalStorage(localStorageKey) ?? [];
  const validValues = storedValues.filter((value: string) => isUUID(value));

  if (validValues.length === storedValues.length) {
    return storedValues;
  }

  try {
    LocalStorageService.setArrayAsLocalStorage(localStorageKey, validValues);
  } catch {
    // Storage can be unavailable (private mode, exhausted quota). Redux still
    // gets the cleaned list; the write only heals the persisted copy.
  }

  log.warn({
    breadcrumb: {
      category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
      event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
      message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
      type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
    },
    data: {
      droppedValues: storedValues.filter((value: string) => !isUUID(value)),
      localStorageKey,
    },
    message: `Dropped ${storedValues.length - validValues.length} stale non-UUID value(s) from persisted filter "${localStorageKey}"`,
  });

  return validValues;
}
